import type { JSX } from "react";
import Image from "next/legacy/image";
import { H1Large } from "../atoms/Heading";
import { P1, Speech } from "../atoms/Paragraph";
import styled from "styled-components";
import { Button } from "../atoms/Button";
import { smoothScrollTo } from "@utils";
import { StandardSection } from "../templates/StandardSection";
import { Fullscreen } from "../templates/CommonStyles";
import { tablet, mobile } from "../templates/Breakpoints";
import GiraffeFacingLeftSvg from "../../public/static/img/rebrand/svgs/GiraffeFacingLeft.svg";

interface Props {
  title: string;
  description: string | JSX.Element;
  callToAction: string;
  backgroundColor: string;
  foreground?: JSX.Element;
  background: JSX.Element;
  clouds: JSX.Element;
}

export const WhyHero = ({
  title,
  description,
  callToAction,
  backgroundColor,
  background,
  clouds,
}: Props) => (
  <Fullscreen background={backgroundColor}>
    {clouds || null}
    {background}
    <ForegroundWrapper>
      <Image src={GiraffeFacingLeftSvg} alt="Yugi, YuLife’s giraffe mascot" />
      <StyledSpeech textAlign="right">Love being yu!</StyledSpeech>
    </ForegroundWrapper>
    <StandardSection>
      <Padding>
        <TextContainer>
          <Title>
            <H1Large>{title}</H1Large>
          </Title>
          <Description>{description}</Description>
          <Button onClick={() => smoothScrollTo(window, "main")}>{callToAction}</Button>
        </TextContainer>
      </Padding>
    </StandardSection>
  </Fullscreen>
);

const Padding = styled.div`
  padding: 2.5rem 0;

  ${tablet} {
    padding: 5rem 0;
  }

  ${mobile} {
    padding: 8rem 0;
  }

  @media only screen and (max-width: 30rem) {
    padding: 2rem 0;
  }
`;

const ForegroundWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  right: 5%;
  transform-origin: 100% 100%;

  @media only screen and (max-width: 90rem) {
    transform: scale(0.7);
    right: 0;
  }

  @media only screen and (max-width: 80rem) {
    transform: scale(0.6);
    right: 0;
  }

  @media only screen and (max-width: 52rem) {
    display: none;
  }
`;

const TextContainer = styled.div`
  position: relative;
  max-width: 80rem;

  @media only screen and (max-width: 30rem) {
    padding-top: 3rem;
  }
`;

const Title = styled.div`
  max-width: 40rem;
  margin-bottom: 1.5rem;
  margin-right: 20rem;
  text-align: left;

  @media only screen and (max-width: 60rem) {
    padding-bottom: 0.5rem;
  }

  @media only screen and (max-width: 52rem) {
    margin-right: 0;
  }
`;

const Description = styled(P1)`
  max-width: 40rem;
  text-align: left;
  margin-bottom: 2rem;
  margin-right: 20rem;
  padding: 0;

  @media only screen and (max-width: 60rem) {
    padding-bottom: 1.5rem;
  }

  @media only screen and (max-width: 52rem) {
    margin-right: 0;
  }
`;

const StyledSpeech = styled(Speech)`
  width: 8rem;
  transform: translate(-24rem, -30rem);

  @media only screen and (max-width: 80rem) {
    display: none;
  }
`;
